import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Button,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Typography,
} from "@mui/material";
import ShareOutlinedIcon from "@mui/icons-material/ShareOutlined";
import { Link } from "react-router-dom";
import { publicApi } from "../../api/http";
import { DEFAULT_PLAYER_IMAGE } from "../DraftOdds/DraftOddsComponents";
import ChartShareModal from "../ChartShareModal/ChartShareModal";
import "./TeamPreDebutArrival.css";

const COLUMNS = {
    rank: "rank",
    player: "player",
    position: "position",
    draft_year: "draft_year",
    overall_pick: "overall_pick",
    kind: "kind",
    dy0: "dy0",
};

const INITIAL_VISIBLE_ROWS = 10;

function formatPct(value) {
    if (value == null || Number.isNaN(Number(value))) return "—";
    return `${(Number(value) * 100).toFixed(1)}%`;
}

function playerName(row) {
    return [row.first_name, row.last_name].filter(Boolean).join(" ") || row.player_id;
}

function playerSortKey(row) {
    return `${row.last_name || ""} ${row.first_name || ""}`.trim().toLowerCase();
}

function compareRows(a, b, orderBy, order) {
    const dir = order === "asc" ? 1 : -1;
    let cmp = 0;

    switch (orderBy) {
        case COLUMNS.player:
            cmp = playerSortKey(a).localeCompare(playerSortKey(b), undefined, {
                sensitivity: "base",
            });
            break;
        case COLUMNS.position:
            cmp = String(a.position || "").localeCompare(String(b.position || ""), undefined, {
                sensitivity: "base",
            });
            break;
        case COLUMNS.kind:
            cmp = String(a.kind || "").localeCompare(String(b.kind || ""), undefined, {
                sensitivity: "base",
            });
            break;
        case COLUMNS.draft_year:
            cmp = (a.draft_year ?? -1) - (b.draft_year ?? -1);
            break;
        case COLUMNS.overall_pick:
            cmp = (a.overall_pick ?? 10_000) - (b.overall_pick ?? 10_000);
            break;
        case COLUMNS.rank:
            cmp = (a.rank ?? 10_000) - (b.rank ?? 10_000);
            break;
        case COLUMNS.dy0:
        default:
            cmp = (a.dy0 ?? -1) - (b.dy0 ?? -1);
            break;
    }

    if (cmp !== 0) return cmp * dir;
    return playerSortKey(a).localeCompare(playerSortKey(b));
}

function TeamPreDebutArrival({ teamId }) {
    const [loading, setLoading] = useState(true);
    const [prospects, setProspects] = useState([]);
    const [orderBy, setOrderBy] = useState(COLUMNS.dy0);
    const [order, setOrder] = useState("desc");
    const [showAll, setShowAll] = useState(false);
    const [shareOpen, setShareOpen] = useState(false);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setShowAll(false);
        setProspects([]);
        publicApi
            .get(`/prospect_preview/${teamId}`, {
                params: { pre_debut_only: true, sort: "dy0" },
            })
            .then((response) => {
                if (cancelled) return;
                const rows = (response.data?.prospects || []).filter(
                    (row) =>
                        !row.has_nhl_games &&
                        /^M[0-5]$/.test(row.kind || "")
                );
                setProspects(rows);
                setLoading(false);
            })
            .catch(() => {
                if (cancelled) return;
                setProspects([]);
                setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [teamId]);

    const sorted = useMemo(
        () => [...prospects].sort((a, b) => compareRows(a, b, orderBy, order)),
        [prospects, orderBy, order]
    );

    const visible = showAll ? sorted : sorted.slice(0, INITIAL_VISIBLE_ROWS);
    const hasMore = sorted.length > INITIAL_VISIBLE_ROWS;
    const canShare = !loading && sorted.length > 0;

    const shareMeta = useMemo(
        () => ({
            cardPath: `/share/teams/${teamId}/predebut-arrival/card`,
            sharePath: `/share/teams/${teamId}/predebut-arrival`,
            title: "Share pre-debut arrival top 5",
            description:
                "Download or share this team-themed card of the top five pre-debut rights holders by NHL debut odds.",
            fileName: `${teamId}-predebut-arrival.png`,
        }),
        [teamId]
    );

    const handleRequestSort = (property) => {
        if (orderBy === property) {
            setOrder((prev) => (prev === "asc" ? "desc" : "asc"));
            return;
        }
        setOrderBy(property);
        setOrder(property === COLUMNS.dy0 || property === COLUMNS.draft_year ? "desc" : "asc");
    };

    const cellSx = {
        borderColor: "var(--border-subtle)",
        color: "var(--text-primary)",
        fontSize: "0.82rem",
        py: 1.15,
        px: 1.5,
    };

    const headSx = {
        ...cellSx,
        fontSize: "0.68rem",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        color: "var(--text-muted)",
        whiteSpace: "nowrap",
        borderBottom: "1px solid var(--border-subtle)",
    };

    const sortLabelSx = {
        color: "inherit",
        "&.Mui-active": { color: "var(--text-primary)" },
        "& .MuiTableSortLabel-icon": { color: "var(--text-muted) !important" },
    };

    return (
        <Grid item xs={12}>
            <div className="container team-predebut-section">
                <div className="team-predebut-header">
                    <div>
                        <Typography className="team-predebut-title">
                            Pre-debut arrival odds
                        </Typography>
                        <Typography className="team-predebut-subtitle">
                            Players this club currently holds NHL rights to who have not debuted,
                            ranked by chance of an NHL debut this season from each player&apos;s
                            most relevant model stage.{" "}
                            <Link to="/how-projections-work" className="team-predebut-method-link">
                                How projections work
                            </Link>
                        </Typography>
                    </div>
                    <div className="team-predebut-header-actions">
                        {!loading ? (
                            <Typography className="team-predebut-meta">
                                {sorted.length} pre-debut · by NHL rights
                            </Typography>
                        ) : null}
                        {canShare ? (
                            <Button
                                size="small"
                                variant="outlined"
                                startIcon={<ShareOutlinedIcon />}
                                className="team-predebut-share-btn"
                                onClick={() => setShareOpen(true)}
                            >
                                Share top 5
                            </Button>
                        ) : null}
                    </div>
                </div>

                {loading ? (
                    <Typography className="team-predebut-empty">Loading arrival odds…</Typography>
                ) : sorted.length === 0 ? (
                    <Typography className="team-predebut-empty">
                        No pre-debut players with NHL rights projections for this team yet.
                    </Typography>
                ) : (
                    <>
                        <TableContainer className="team-predebut-table-wrap">
                            <Table
                                className="team-predebut-table"
                                size="small"
                                aria-label="Pre-debut arrival odds by NHL rights"
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={headSx} align="right">
                                            <TableSortLabel
                                                active={orderBy === COLUMNS.rank}
                                                direction={
                                                    orderBy === COLUMNS.rank ? order : "asc"
                                                }
                                                onClick={() => handleRequestSort(COLUMNS.rank)}
                                                sx={sortLabelSx}
                                            >
                                                #
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={headSx}>
                                            <TableSortLabel
                                                active={orderBy === COLUMNS.player}
                                                direction={
                                                    orderBy === COLUMNS.player ? order : "asc"
                                                }
                                                onClick={() => handleRequestSort(COLUMNS.player)}
                                                sx={sortLabelSx}
                                            >
                                                Player
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={headSx}>
                                            <TableSortLabel
                                                active={orderBy === COLUMNS.position}
                                                direction={
                                                    orderBy === COLUMNS.position ? order : "asc"
                                                }
                                                onClick={() => handleRequestSort(COLUMNS.position)}
                                                sx={sortLabelSx}
                                            >
                                                Pos
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={headSx}>
                                            <TableSortLabel
                                                active={orderBy === COLUMNS.draft_year}
                                                direction={
                                                    orderBy === COLUMNS.draft_year
                                                        ? order
                                                        : "desc"
                                                }
                                                onClick={() =>
                                                    handleRequestSort(COLUMNS.draft_year)
                                                }
                                                sx={sortLabelSx}
                                            >
                                                Draft
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={headSx} align="right">
                                            <TableSortLabel
                                                active={orderBy === COLUMNS.overall_pick}
                                                direction={
                                                    orderBy === COLUMNS.overall_pick
                                                        ? order
                                                        : "asc"
                                                }
                                                onClick={() =>
                                                    handleRequestSort(COLUMNS.overall_pick)
                                                }
                                                sx={sortLabelSx}
                                            >
                                                Overall
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={headSx}>
                                            <TableSortLabel
                                                active={orderBy === COLUMNS.kind}
                                                direction={
                                                    orderBy === COLUMNS.kind ? order : "asc"
                                                }
                                                onClick={() => handleRequestSort(COLUMNS.kind)}
                                                sx={sortLabelSx}
                                            >
                                                Model
                                            </TableSortLabel>
                                        </TableCell>
                                        <TableCell sx={headSx} align="right">
                                            <TableSortLabel
                                                active={orderBy === COLUMNS.dy0}
                                                direction={
                                                    orderBy === COLUMNS.dy0 ? order : "desc"
                                                }
                                                onClick={() => handleRequestSort(COLUMNS.dy0)}
                                                sx={sortLabelSx}
                                            >
                                                Debut %
                                            </TableSortLabel>
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {visible.map((row, index) => (
                                        <TableRow key={row.player_id}>
                                            <TableCell sx={cellSx} align="right">
                                                {row.rank ?? index + 1}
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <Link
                                                    to={`/player/${row.player_id}?view=post-draft&tab=arrival`}
                                                    className="team-predebut-player"
                                                >
                                                    <img
                                                        src={
                                                            row.player_image_url ||
                                                            DEFAULT_PLAYER_IMAGE
                                                        }
                                                        alt=""
                                                        className="team-predebut-player-img"
                                                    />
                                                    <span>{playerName(row)}</span>
                                                </Link>
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                {row.position || "—"}
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                {row.draft_year ?? "—"}
                                            </TableCell>
                                            <TableCell sx={cellSx} align="right">
                                                {row.overall_pick != null
                                                    ? row.overall_pick
                                                    : "—"}
                                            </TableCell>
                                            <TableCell sx={cellSx}>
                                                <span className="team-predebut-kind">
                                                    {row.kind || "—"}
                                                    {row.is_dy0_stage ? (
                                                        <span className="team-predebut-kind-live">
                                                            {" "}
                                                            live
                                                        </span>
                                                    ) : null}
                                                </span>
                                            </TableCell>
                                            <TableCell
                                                sx={cellSx}
                                                align="right"
                                                className="team-predebut-pct"
                                            >
                                                {formatPct(row.dy0 ?? row.p_nhl_next_year)}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        {hasMore ? (
                            <Box className="team-predebut-more">
                                <Typography className="team-predebut-more-meta">
                                    Showing {visible.length} of {sorted.length}
                                </Typography>
                                <Button
                                    size="small"
                                    variant="text"
                                    onClick={() => setShowAll((value) => !value)}
                                    className="team-predebut-more-btn"
                                >
                                    {showAll
                                        ? `Show fewer (${INITIAL_VISIBLE_ROWS})`
                                        : `Show all ${sorted.length}`}
                                </Button>
                            </Box>
                        ) : null}
                    </>
                )}
            </div>
            <ChartShareModal
                open={shareOpen}
                onClose={() => setShareOpen(false)}
                cardPath={shareMeta.cardPath}
                sharePath={shareMeta.sharePath}
                title={shareMeta.title}
                description={shareMeta.description}
                fileName={shareMeta.fileName}
            />
        </Grid>
    );
}

export default TeamPreDebutArrival;
